/**
 * Every table stacks on a phone, and this is the half CSS cannot do alone.
 *
 * Jonathan, 2026-09-18, on the first phone build: *"the tables are bad. need
 * to scroll horizontal too much."* He was right -- a six-column ledger
 * scrolled sideways under a shadow is still a six-column ledger, and a phone
 * reader never finds the fourth column. So under 640px `styles.css` turns
 * every `<tr>` into a card: the first cell is the title and every other cell
 * is a label over a value, the same shape as `.tile-facts`.
 *
 * The label is the column's own header, which CSS can only reach as
 * `attr(data-label)` on the cell -- and fifteen tables across the app do not
 * carry one. Rather than thread `data-label` through every `<td>` in the
 * codebase (and every one written after this), the shell runs this once on
 * mount and again whenever the page's DOM changes, copying each `<th>`'s text
 * onto the cells beneath it. React does not manage attributes it did not
 * render, so the copies survive re-renders; the observer watches structure,
 * not attributes, so setting them cannot re-trigger it.
 *
 * No colspan anywhere in the app (checked 2026-09-18); if one arrives the
 * index walk below is what has to learn about it.
 */

function textOf(node: Element): string {
  return node.textContent?.trim() ?? "";
}

export function labelCells(root: ParentNode): void {
  for (const table of root.querySelectorAll("table")) {
    const heads = Array.from(table.querySelectorAll(":scope > thead th")).map(textOf);
    if (heads.length === 0) continue;
    for (const row of table.querySelectorAll(":scope > tbody > tr")) {
      Array.from(row.children).forEach((cell, i) => {
        const label = heads[i] ?? "";
        if (cell.getAttribute("data-label") !== label) cell.setAttribute("data-label", label);
      });
    }
  }
}

/**
 * Keep `root` labelled as its contents change. Returns the stop function.
 * Batched to one pass per frame: a page mounting a table fires one mutation
 * per row, and one walk after all of them is enough.
 */
export function watchCells(root: Element): () => void {
  labelCells(root);
  let queued = 0;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = requestAnimationFrame(() => {
      queued = 0;
      labelCells(root);
    });
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  return () => {
    observer.disconnect();
    if (queued) cancelAnimationFrame(queued);
  };
}
